import { cn, Loader, Tooltip } from "@cloudflare/kumo";
import {
  CheckCircleIcon,
  CircleNotchIcon,
  PauseIcon,
  SpinnerIcon,
  StopIcon,
  WarningCircleIcon
} from "@phosphor-icons/react";
import type { WorkflowRuntimeStatus } from "@workerstation/shared";

// Adapted from workers-sdk's local-explorer-ui workflow StatusBadge and
// StatusIcon, keyed to this runtime's WorkflowRuntimeStatus union.

const statusStyles: Record<WorkflowRuntimeStatus, string> = {
  complete: "bg-kumo-badge-teal text-white",
  errored: "bg-kumo-badge-red text-white",
  terminated: "bg-kumo-badge-red text-white",
  waiting: "bg-kumo-brand text-white",
  paused: "bg-kumo-brand text-white",
  running: "bg-kumo-brand text-white",
  waitingForPause: "bg-kumo-brand text-white",
  rollingBack: "bg-kumo-brand text-white",
  queued: "bg-kumo-subtle text-white",
  unknown: "bg-kumo-subtle text-white"
};

const statusLabels: Record<WorkflowRuntimeStatus, string> = {
  queued: "Queued",
  running: "Running",
  paused: "Paused",
  errored: "Errored",
  terminated: "Terminated",
  complete: "Complete",
  waitingForPause: "Waiting for Pause",
  rollingBack: "Rolling Back",
  waiting: "Waiting",
  unknown: "Unknown"
};

function resolveStatus(
  status: WorkflowRuntimeStatus | string | undefined
): WorkflowRuntimeStatus {
  return status && status in statusStyles
    ? (status as WorkflowRuntimeStatus)
    : "unknown";
}

export function WorkflowStatusBadge({
  status
}: {
  status: WorkflowRuntimeStatus | string | undefined;
}) {
  const resolved = resolveStatus(status);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs",
        statusStyles[resolved]
      )}
    >
      {statusLabels[resolved]}
    </span>
  );
}

export function WorkflowStatusIcon({
  status
}: {
  status: WorkflowRuntimeStatus | string;
}) {
  switch (status) {
    case "complete":
      return (
        <Tooltip content="Complete">
          <CheckCircleIcon size={20} weight="fill" className="text-kumo-link" />
        </Tooltip>
      );
    case "errored":
      return (
        <Tooltip content="Errored">
          <WarningCircleIcon
            size={20}
            weight="fill"
            className="text-kumo-danger"
          />
        </Tooltip>
      );
    case "terminated":
      return (
        <Tooltip content="Terminated">
          <StopIcon size={20} weight="fill" className="text-kumo-subtle" />
        </Tooltip>
      );
    case "paused":
    case "waitingForPause":
      return (
        <Tooltip content="Paused">
          <PauseIcon size={20} weight="fill" className="text-kumo-subtle" />
        </Tooltip>
      );
    case "running":
    case "rollingBack":
      return (
        <Tooltip content={statusLabels[status]}>
          <Loader size={20} />
        </Tooltip>
      );
    case "waiting":
      return (
        <Tooltip content="Waiting">
          <SpinnerIcon size={20} weight="bold" className="text-kumo-subtle" />
        </Tooltip>
      );
    default:
      return (
        <Tooltip content={status}>
          <CircleNotchIcon
            size={20}
            weight="bold"
            className="text-kumo-subtle"
          />
        </Tooltip>
      );
  }
}
